import '../../sass/BusinessContractsAudit/index.scss'
import {fadeAnimate} from '../components/fadeAnimate'
import {scrollToElementWithOffset} from '../components/scrollToElementWithOffset'

// Question data with explanations for the report
const questionData = {
    q1: {
        title: "Juhatuse liikme leping",
        recommendation: "Sõlmige juhatuse liikmetega kindlasti kirjalik juhatuse liikme leping.",
        explanation: `<p><strong>Miks see on oluline?</strong></p>
<p>Paljud ettevõtted tegutsevad nii, et juhatuse liikmetel kas puudub leping üldse või kasutatakse selleks töölepingut. See ei ole korrektne ega kooskõlas seadusega ning põhjustab sageli hilisemaid vaidlusi.</p>
<p>Juhatuse liige <strong>ei saa täita oma ülesandeid töölepingu alusel</strong> – töölepingu seadus välistab selle otsesõnu. Samuti ei kohaldu juhatuse liikmetele automaatselt töötajatele ette nähtud õigused, nagu:</p>
<ul>
<li>õigus tasule ja selle maksmise kord</li>
<li>puhkuse reeglid</li>
<li>töö- ja puhkeaja regulatsioon</li>
<li>töökorralduse juhised</li>
</ul>
<p>Kõik need tingimused tuleb seetõttu <strong>selgelt ja üheselt kokku leppida juhatuse liikme lepingus</strong>, vastasel juhul jäävad need määramatuks ja võivad tuua kaasa arusaamatusi.</p>
<p>Lisaks on juhatuse liikme seadusest tulenevad õigused, kohustused ja vastutus ulatuslikud. Kirjalik leping:</p>
<ul>
<li>täpsustab ootused ja rollijaotuse</li>
<li>aitab vähendada juhatuse liikme isikliku vastutuse riski</li>
<li>loob selguse tasustamise, hüvede ja töökorralduse osas</li>
<li>aitab vältida hilisemaid vaidlusi ja tõlgendamisprobleeme</li>
</ul>
<strong>Hästi koostatud juhatuse liikme leping kaitseb nii ettevõtte kui juhatuse liikme huve ja tagab professionaalse juhtimise.</strong>`
    },
    q2_1: {
        title: "Osanike leping",
        recommendation: "Sõlmige osanikega kindlasti kirjalik ja läbimõeldud osanike leping.",
        explanation: `<p><strong>Miks see on oluline?</strong></p>
<p>Osanike leping on üks olulisemaid dokumente mitme osanikuga äriühingus. Kuigi seadus reguleerib teatud suhteid osanike vahel, jätab see palju olulist osapoolte endi otsustada. Kui kirjalikku kokkulepet ei ole, võivad erimeelsused kiiresti kasvada vaidlusteks, mis halvavad ettevõtte tegevuse.</p>
<p>Kirjalik osanike leping aitab:</p>
<ul>
<li>vältida konflikte osanike rollide, õiguste ja kohustuste üle;</li>
<li>kokku leppida otsuste tegemise korra, sh häälteenamuse, vetoõigused ja strateegilised otsused;</li>
<li>sätestada reeglid osaluse võõrandamiseks, sealhulgas eelisostuõigused, müügipiirangud, kaasamüügi- ja kaasaostukohustused (drag/tag-along);</li>
<li>kaitsta ettevõtet ja partnereid ootamatuste eest, nt kui üks osanik lahkub, haigestub, ei panusta lubatud mahus või rikub kokkuleppeid;</li>
<li>kokku leppida kasumi jaotamise kord ning osapoolte panused ettevõttesse;</li>
<li>tagada juhtimise stabiilsus ja vältida olukorda, kus otsused jäävad seisma või juhatus tegutseb ebakindluses;</li>
<li><strong>kehtestada konkurentsikeeld ning kaitsta ettevõtte ärisaladusi ja intellektuaalset omandit</strong>, et ükski osanik ei looks paralleelset äri ega kasutaks ühingu teadmisi ebaausalt;</li>
<li><strong>leppida kokku patiseisu (deadlock) lahendamise mehhanismid</strong>, mis aitavad vältida olukorda, kus ettevõtte töö seiskub osanike erimeelsuste tõttu;</li>
<li><strong>kirjeldada osanike panused ja rollid</strong>, mis suurendab läbipaistvust ja vähendab eri ootustest tekkinud pingeid.</li>
</ul>
<p><strong>Hästi koostatud osanike leping loob kindluse, tugevdab koostööd ning kaitseb nii ettevõtte kui igapäevaselt panustavate osanike huve.</strong></p>`
    },
    q3: {
        title: "Põhikiri",
        recommendation: "Viige põhikiri vastavusse äriühingu tegeliku olukorra ja vajadustega.",
        explanation: `<p><strong>Miks see on oluline?</strong></p>
<p>Põhikiri on ühingu ametlik alusdokument, mis peab kajastama:</p>
<ul>
<li>osakapitali suurust ja selle struktuuri;</li>
<li>juhatuse koosseisu ja valimise korda;</li>
<li>otsuste tegemise korda;</li>
<li>osanike õigusi ja kohustusi (ka ühe osanikuga ühingus: nt kasumijaotus, juhatuse volitused);</li>
<li>osaluste kohta käivaid reegleid (nt võõrandamine, eelisostuõigus);</li>
<li>muid äriplaanist ja praktikast tulenevaid vajadusi.</li>
</ul>

<p>Põhikirja tuleb ajakohastada näiteks siis, kui:</p>
<ul>
<li>osakapital on muutunud;</li>
<li>juhatuse ülesehitus või volitused on muutunud;</li>
<li>ühingu tegevusala on muutunud;</li>
<li>soovitakse selgemalt reguleerida näiteks dividendide jaotamist;</li>
<li>vajatakse erisusi, mida seadus lubab, kuid vaikimisi ei rakenda.</li>
</ul>

<p>Ajakohane põhikiri tagab sujuva juhtimise, väldib vaidlusi ja lihtsustab toiminguid registris ning partneritega.</p>`
    },
    q4: {
        title: "Osanike otsuste dokumendipõhjad",
        recommendation: "Hankige korrektsed dokumendipõhjad osanike otsuste vastuvõtmiseks.",
        explanation: `<p><strong>Miks see on oluline?</strong></p>
<p>Osanike otsuste vormistamine peab vastama seaduses sätestatud nõuetele. Korrektsed dokumendipõhjad aitavad:</p>
<ul>
<li>tagada, et otsused on vormiliselt korrektsed ja kehtivad;</li>
<li>vältida vigadest tingitud viivitusi registritoimingutes;</li>
<li>dokumenteerida olulised otsused usaldusväärselt ja tõenduslikult;</li>
<li>hoida ettevõtte juhtimisdokumentatsioon ühtsel ja professionaalsel tasemel.</li>
</ul>
<p>Põhivormide hulka kuuluvad:</p>
<ul>
<li>osanike otsuse vorm;</li>
<li>koosoleku protokolli näidis;</li>
<li>koosoleku ja korduskoosoleku kutsete näidised;</li>
<li>hääletusprotokollid ja oluliste otsuste eraldi vormid.</li>
</ul>

<p>Õiged vormid muudavad ettevõtte juhtimise sujuvamaks ja vähendavad õigusriske.</p>`
    },
    q5: {
        title: "Töölepingud ja lisadokumendid",
        recommendation: "Vaadake üle töölepingud ja nendega seotud dokumendid ning kohandage need vastavusse ametikoha tegelike tingimuste, töökorralduse ja seaduse nõuetega.",
        explanation: `<p><strong>Miks see on oluline?</strong></p>
<p>Töölepingu seadus nõuab, et töötajale antakse kirjalikult kogu oluline teave töötingimuste kohta. Üldised või aegunud mallid ei pruugi neid nõudeid täita. Individualiseeritud tööleping koos vajalike lisadokumentidega (nt ametijuhend, töökorralduse reeglid, varalise vastutuse kokkulepped) aitab:</p>
<ul>
<li>vältida töövaidlusi;</li>
<li>määratleda selgelt ülesanded ja vastutuse;</li>
<li>tõendada tööandja nõuete täitmist;</li>
<li>vähendada õigusriske ja trahvide võimalust;</li>
<li>tagada sujuv ja korrektne töösuhe.</li>
</ul>`
    },
    q6: {
        title: "Laenuleping",
        recommendation: "Hankige laenuleping, mis reguleerib selgelt laenusumma, intressi, tagasimakse korra, tähtaja, tagatised ja muud olulised tingimused.",
        explanation: `<p><strong>Miks see on oluline?</strong></p>
<p>Raha laenamine on ettevõtluses üks enamlevinumaid toiminguid. Peaaegu iga ettevõtja satub mingil hetkel olukorda, kus tal on vaja ise laenu võtta või anda lühiajaliselt laenu mõnele koostööpartnerile või teisele ettevõttele. Kui laenulepingu tingimused ei ole korralikult läbi mõeldud ja kirjalikult kokku lepitud, võib see kaasa tuua mitmeid riske, sealhulgas:</p>
<ul>
<li><strong>maksuriskid</strong>, mis võivad tekkida juhul, kui laenutehingu tingimused ei ole turupärased;</li>
<li><strong>tõendamisraskused</strong>, kui tekib vaidlus intressi, tagasimaksekorra või tähtaja üle;</li>
<li><strong>laenu tagasisaamise probleemid</strong>, eriti kui puuduvad selged maksegraafikud või tagatised;</li>
<li><strong>osapooltevahelised konfliktid</strong>, mis võivad tekkida ebaselgete kohustuste või ootuste tõttu;</li>
<li><strong>ettevõtte finantsjuhtimise ebakindlus</strong>, kui laenud ei ole nõuetekohaselt dokumenteeritud;</li>
<li><strong>õigusvaidlused</strong>, mis on ajakulukad ja võivad majanduslikult kahjustada mõlemat poolt.</li>
</ul>

<p>Seetõttu on oluline, et ettevõttel oleks korrektselt koostatud ja läbimõeldud laenuleping, mis sätestab kõik olulised tingimused: laenusumma, intress, tagasimakse tähtaeg ja kord, tagatised ning muud kokkulepped. Hästi vormistatud laenuleping aitab vähendada nii finants- kui õigusriske ja tagab, et tehing on mõlema poole jaoks selge ja kaitstud.</p>`
    },
    q7: {
        title: "Konfidentsiaalsusleping (NDA)",
        recommendation: "Kasutage ettevõtte ärisaladuste kaitsmiseks läbimõeldud ja teie tegevusega kooskõlas olevat konfidentsiaalsuslepingut.",
        explanation: `<p><strong>Miks see on oluline?</strong></p>

<p>Uue koostööpartneri, teenuseosutaja või potentsiaalse äritehingu osapoolega läbirääkimiste alustamine eeldab väga sageli seda, et peate jagama oma äritegevuse kohta tundlikku infot – näiteks kliendibaasi andmeid, hinnastust, tööprotsesse, tehnilisi lahendusi või äriplaane. Sellises olukorras on oluline, et enne teabe avaldamist oleks sõlmitud konfidentsiaalsusleping.</p>
<p>Konfidentsiaalsusleping aitab:</p>
<ul>
<li>vältida ärisaladuste ja tundliku info lekkimist või väärkasutamist;</li>
<li>kaitsta ettevõtte know-how’d ja konkurentsieelist;</li>
<li>luua turvalise raamistiku läbirääkimisteks ja uue koostöö alustamiseks;</li>
<li>tagada, et teine pool ei saa jagatud infot kasutada ettevõtte kahjuks;</li>
<li>tugevdada teie positsiooni võimaliku vaidluse korral.</li>
</ul>

<p>Hästi koostatud konfidentsiaalsusleping on üks lihtsamaid ja tõhusamaid viise maandada riske, mis tekivad tundliku info jagamisel väliste isikutega.</p>`
    },
    q8_1: {
        title: "Privaatsuspoliitika",
        recommendation: "Avaldage oma veebilehel läbimõeldud ja nõuetele vastav privaatsuspoliitika.",
        explanation: `<p><strong>Miks see on oluline?</strong></p>
<p>Privaatsuspoliitika on kohustuslik igale veebilehele, kui toimub kasvõi minimaalne isikuandmete kogumine. Praktikas tähendab see, et privaatsuspoliitikat on vaja peaaegu alati – näiteks kui:</p>
<ul>
<li>kogutakse külastaja nime, e-posti aadressi või muid andmeid kontaktivormi kaudu;</li>
<li>kasutatakse analüütikaküpsiseid või muid jälgimistehnoloogiaid;</li>
<li>veebileht võimaldab jätta kommentaare või üles laadida sisu;</li>
<li>kogutakse IP-aadresse, seadmeandmeid või muid käitumismustreid.</li>
</ul>
<p>Isikuandmete mõiste on väga lai: isikuandmed on sisuliselt iga teave, mille põhjal saab inimest otseselt või kaudselt tuvastada. Seetõttu eeldab peaaegu igasugune veebilehe kasutajalt saadav info – isegi tehnilised logid – privaatsuspoliitika olemasolu.
GDPR nõuab, et veebilehe omanik annaks andmesubjektidele (veebilehe külastajatele) selgelt ja arusaadavalt vähemalt järgmise info:</p>
<ul>
<li>kes nende andmeid töötleb ja kellel võib olla neile juurdepääs;</li>
<li>milliseid andmeid kogutakse, mis eesmärgil ja mis õiguslikul alusel;</li>
<li>teave edastamiste kohta, sh kui andmeid liigutatakse väljapoole Euroopa Majanduspiirkonda;</li>
<li>kui kaua andmeid säilitatakse;</li>
<li>millised on isiku õigused oma andmete suhtes, sh õigus nõusolek tagasi võtta ja kaebus esitada järelevalveasutusele;</li>
<li>kust andmeid saadakse, kui need ei pärine otse andmesubjektilt;</li>
<li>kas toimub profileerimine või automatiseeritud otsustamine ning milline on selle loogika ja võimalik mõju;</li>
<li>andmekaitseametniku kontaktandmed (kui ettevõte on kohustatud sellise ametniku määrama).</li>
</ul>

<p>Lühidalt: privaatsuspoliitika peab selgitama, kes, mis andmeid, mis alusel, kui kaua ja kuidas töötleb ning millised õigused on isikul enda andmete suhtes.</p>

<p>Korrektselt koostatud privaatsuspoliitika vähendab õiguslikke riske, tõstab läbipaistvust ning suurendab kliendi usaldust ettevõtte vastu.</p>`
    },
    q8_2: {
        title: "Teenusetingimused",
        recommendation: "Avaldage oma veebilehe kasutamiseks selged ja läbimõeldud teenusetingimused.",
        explanation: `<p><strong>Miks see on oluline?</strong></p>
<p>Kui ettevõte osutab oma veebilehe kaudu teenust või pakub platvormi, kus kasutajad saavad kontot luua, sisu kasutada või toiminguid teha, on <strong>teenusetingimused ettevõtte ja kasutaja vahelise suhte keskne dokument</strong>. Ilma teenusetingimusteta jääb suur osa õigustest ja kohustustest määramata, mis võib viia vaidlusteni või isegi vastutusriskideni.</p>
<p>Hästi koostatud teenusetingimused aitavad:</p>
<ul>
<li>määratleda <strong>teenuse ulatuse</strong>, funktsionaalsuse ja kasutamise reeglid;</li>
<li>kehtestada <strong>kasutajate kohustused</strong>, keelatud tegevused ja süsteemi turvalise kasutamise põhimõtted;</li>
<li>selgitada teenusepakkuja õigusi teenuse muutmisel, katkestamisel või lõpetamisel;</li>
<li>sätestada <strong>vastutuse piirangud</strong>, mis võivad ennetada olulisi rahalisi riske;</li>
<li>selgitada intellektuaalomandi kuuluvust (andmed, sisu, tarkvara, kaubamärgid);</li>
<li>sätestada kaebuste lahendamise korra ja kohaldatava õiguse;</li>
<li>koordineerida seost privaatsuspoliitika, küpsiste reeglite ja muu infoga.</li>
</ul>
<p>Paljud ettevõtted ekslikult usuvad, et “tavalise informatiivse veebilehe” puhul teenusetingimusi vaja ei ole. Tegelikult on need vajalikud alati, kui:</p>
<ul>
<li>veebilehel saab midagi tellida või osta;</li>
<li>kasutajad saavad end registreerida või kontot luua;</li>
<li>osutatakse digiteenuseid või ligipääsu tarkvarale;</li>
<li>pakutakse sisu, mille kasutamist on vaja juriidiliselt reguleerida;</li>
<li>veebilehel on interaktiivsed funktsioonid (nt kommentaarid, laadimised, API jmt).</li>
</ul>
<p>Korrektsed teenusetingimused vähendavad õiguslikku riski, piiravad teenusepakkuja vastutust ja loovad kasutajatele selged ootused.</p>`
    },
    q9: {
        title: "Peamine teenuse- või müügileping",
        recommendation: "Kasutage oma teenuse või kauba müümiseks lepingut, mis on spetsiaalselt teie ettevõtte tegevuse, riskide ja ärimudeli jaoks koostatud.",
        explanation: `<p><strong>Miks see on oluline?</strong></p>
<p>Peamine teenuse- või müügileping on sageli ettevõtte <strong>kõige olulisem äridokument</strong>, sest just selle kaudu võetakse vastu tellimusi, osutatakse teenuseid, müüakse kaupu ja teenitakse tulu. Kui leping ei ole läbimõeldud või on laetud internetist juhusliku mallina, tekivad mitmed riskid:</p>
<ul>
<li>leping võib sisaldada aegunud või kehtetuid sätteid;</li>
<li>leping ei pruugi kaitsta teie huve tüüpiliste äriliste riskide eest (maksehäired, viivitused, garantiid, vastutus);</li>
<li>leping võib olla vastuolus seadusega või tüüptingimuste regulatsiooniga;</li>
<li>leping ei arvesta teie konkreetse teenuse või toote eripärasid;</li>
<li>oluline teave (nt vastutuse piirangud, töö ulatus, intellektuaalomandi kuuluvus, andmekaitse) võib puududa või olla ebapiisav.</li>
</ul>
<p>Läbimõeldud ja õigesti koostatud leping aitab:</p>
<ul>
<li>ennetada vaidlusi klientidega;</li>
<li>tagada teenuse või kauba eest tasu laekumise;</li>
<li>piirata ettevõtte vastutust ja maandada riske;</li>
<li>luua professionaalse mulje ja suurendada usaldusväärsust;</li>
<li>vähendada olukordi, kus lepingut tuleb käsitsi iga kord kohandada.</li>
</ul>
<p>Hea leping ei ole pelgalt "vormistus", vaid üks ettevõtte kõige olulisemaid tööriistu oma õiguste kaitsmiseks ja igapäevase äritegevuse sujuvaks toimimiseks.</p>`
    }
};

let currentQuestion = 0;
let answers = {};
let questionFlow = [];

// Initialize answer option click handlers
document.querySelectorAll('.QuizOption').forEach(option => {
    option.addEventListener('click', function() {
        const slide = this.closest('.QuizSlide');
        slide.querySelectorAll('.QuizOption').forEach(opt => opt.classList.remove('Selected'));
        this.classList.add('Selected');
        this.querySelector('input').checked = true;
        slide.querySelector('.Btn--Next').disabled = false;
    });
});


// Start quiz button
document.querySelectorAll('.StartQuiz').forEach(item => {
    item.addEventListener('click', function() {
        document.getElementById('QuizStage0').style.display = 'none';
        document.getElementById('QuizInner').style.display = '';
        document.getElementById('progressContainer').style.display = 'block';
        document.getElementById('resultsScreen').classList.remove('Active');
        buildQuestionFlow();
        showQuestion(0);
    }, { passive: true })
})

// Next question button
document.getElementById('QuizInner').querySelectorAll('.Btn--Next').forEach(item => {
    item.addEventListener('click', function() {
        const slides = document.querySelectorAll('.QuizSlide');
        const currentSlide = slides[questionFlow[currentQuestion]];
        const selectedInput = currentSlide.querySelector('input[type="radio"]:checked');

        if (selectedInput) {
            answers[selectedInput.name] = selectedInput.value;

            if (currentQuestion + 1 < questionFlow.length) {
                updateQuestionFlow();
                showQuestion(currentQuestion + 1);
            } else {
                // Hide questions, show results
                document.getElementById('QuizInner').style.display = 'none';
                document.getElementById('progressContainer').style.display = 'none';
                document.getElementById('resultsScreen').classList.add('Active');

                generateReport();
            }
        }
    }, { passive: true })
})

// Prev question button
document.getElementById('QuizInner').querySelectorAll('.Btn--Prev').forEach(item => {
    item.addEventListener('click', function() {
        if (currentQuestion > 0) {
            showQuestion(currentQuestion - 1);
        }
    }, { passive: true })
})

function buildQuestionFlow() {
    questionFlow = [];
    const slides = document.querySelectorAll('.QuizSlide');
    slides.forEach((slide, index) => {
        const isConditional = slide.dataset.conditional === 'true';
        if (!isConditional) {
            questionFlow.push(index);
        }
    });
}

function updateQuestionFlow() {
    const slides = document.querySelectorAll('.QuizSlide');
    questionFlow = [];

    slides.forEach((slide, index) => {
        const isConditional = slide.dataset.conditional === 'true';
        if (!isConditional) {
            questionFlow.push(index);
        } else {
            const depends = slide.dataset.depends;
            const dependsValue = slide.dataset.dependsValue;
            if (answers[depends] === dependsValue) {
                // Find where to insert this conditional question
                const parentQuestion = slide.dataset.question.split('.')[0];
                const parentIndex = questionFlow.findIndex(i =>
                    slides[i].dataset.question === parentQuestion
                );
                if (parentIndex !== -1) {
                    // Check if already in flow
                    if (!questionFlow.includes(index)) {
                        // Find correct position (after parent and any existing sub-questions)
                        let insertAt = parentIndex + 1;
                        while (insertAt < questionFlow.length &&
                        slides[questionFlow[insertAt]].dataset.question.startsWith(parentQuestion + '.')) {
                            insertAt++;
                        }
                        questionFlow.splice(insertAt, 0, index);
                    }
                }
            }
        }
    });
}

function showQuestion(flowIndex) {
    const slides = document.querySelectorAll('.QuizSlide');
    slides.forEach(slide => slide.classList.remove('Active'));

    if (flowIndex >= 0 && flowIndex < questionFlow.length) {
        const slideIndex = questionFlow[flowIndex];
        slides[slideIndex].classList.add('Active');
        currentQuestion = flowIndex;
        updateProgress();

        // Update back button visibility
        const backBtn = slides[slideIndex].querySelector('.Btn--Prev');
        if (backBtn) {
            backBtn.style.visibility = flowIndex === 0 ? 'hidden' : 'visible';
        }

        // Check if answer was already selected
        const inputName = slides[slideIndex].querySelector('input[type="radio"]').name;
        if (answers[inputName]) {
            const selectedInput = slides[slideIndex].querySelector(`input[value="${answers[inputName]}"]`);
            if (selectedInput) {
                selectedInput.closest('.QuizOption').classList.add('Selected');
                slides[slideIndex].querySelector('.Btn--Next').disabled = false;
            }
        }
    }
}

function updateProgress() {
    const progress = ((currentQuestion + 1) / questionFlow.length) * 100;
    document.getElementById('progressFill').style.transform = `scaleX(${progress}%) translateZ(0)`;
    document.getElementById('progressText').textContent = `Küsimus ${currentQuestion + 1} / ${questionFlow.length}`;
}

function generateReport() {
    let issues = [];
    let okItems = [];

    // Check each answer and categorize
    const checkQuestion = (key, okValue) => {
        if (answers[key] !== undefined) {
            if (answers[key] === okValue) {
                okItems.push(key);
            } else {
                issues.push(key);
            }
        }
    };

    // Q1: Board member agreement
    checkQuestion('q1', 'jah');

    // Q2.1: Shareholders agreement (only if Q2 = jah)
    if (answers.q2 === 'jah') {
        checkQuestion('q2_1', 'jah');
    }

    // Q3-7: Standard questions
    checkQuestion('q3', 'jah');
    checkQuestion('q4', 'jah');
    checkQuestion('q5', 'jah');
    checkQuestion('q6', 'jah');
    checkQuestion('q7', 'jah');

    // Q8.1, Q8.2: Website questions (only if Q8 = jah)
    if (answers.q8 === 'jah') {
        checkQuestion('q8_1', 'jah');
        checkQuestion('q8_2', 'jah');
    }

    // Q9: Main service/sales contract
    checkQuestion('q9', 'jah');

    // Update summary counts
    document.getElementById('issuesCount').textContent = issues.length;
    document.getElementById('okCount').textContent = okItems.length;

    if (issues.length > 0) {
        document.querySelectorAll('.email-capture.js-passed').forEach(el => {
            el.style.display = 'none';
        })

        document.querySelectorAll('.email-capture.js-needs-attention').forEach(el => {
            el.style.display = '';
        })
    } else {
        document.querySelectorAll('.email-capture.js-passed').forEach(el => {
            el.style.display = '';
        })

        document.querySelectorAll('.email-capture.js-needs-attention').forEach(el => {
            el.style.display = 'none';
        })
    }

    // Generate results HTML
    const resultsList = document.getElementById('resultsList');
    let html = '';
    const questionData = JSON.parse(resultsList.getAttribute('data-info'));

    if (issues.length > 0) {
        html += `<div class="result-category"><h3>${questionData.audit_requires_attention}</h3>`;
        issues.forEach(key => {
            const data = questionData[key];
            if (data) {
                html += `
                            <div class="result-item collapsed">
                                <h4><span class="status-icon">⚠</span> ${data.title}</h4>
                                <p class="recommendation">${data.recommendation}</p>
                                <div class="explanation">${data.explanation}</div>
                                <button class="read-more auditReadMore">${questionData.audit_continue_reading}</button>
                            </div>
                        `;
            }
        });
        html += '</div>';
    }

    if (okItems.length > 0) {
        html += `<div class="result-category"><h3>${questionData.audit_passed}</h3>`;
        okItems.forEach(key => {
            const data = questionData[key];
            if (data) {
                html += `
                            <div class="result-item ok collapsed">
                                <h4><span class="status-icon">✓</span> ${data.title}</h4>
                                <p class="recommendation">${data.recommendation}</p>
                                <div class="explanation">${data.explanation}</div>
                                <button class="read-more auditReadMore">${questionData.audit_continue_reading}</button>
                            </div>
                        `;
            }
        });
        html += '</div>';
    }

    resultsList.innerHTML = html;

    document.querySelectorAll('.auditReadMore').forEach((el) => {
        el.addEventListener('click', function() {
            el.parentNode.classList.toggle('collapsed')
        })
    })
}

document.querySelectorAll('.js-audit-request-form').forEach((el) => {
    el.addEventListener('submit', async function(event) {
        event.preventDefault();

        const form = event.target

        const the_success_message = form.parentNode.querySelector('.js_audit_form_success')
        const success_messages = document.querySelectorAll('.js_audit_form_success')
        const error_message = form.parentNode.querySelector('.js_audit_form_error')

        const url = form.getAttribute('action')
        const method = form.getAttribute('method').toUpperCase()
        const formData = new FormData(form)
        Object.keys(answers).forEach((key) => {
            formData.append(key, answers[key]);
        })

        document.querySelectorAll('.js_audit_field').forEach(el => el.disabled = true)
        document.querySelectorAll('.js_audit_submit').forEach(el => el.disabled = true)

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'xmlhttprequest',
                },
                body: formData,
            })

            if (!response.ok) throw await response.json()

            const result = await response.json()
            error_message.style.display = 'none'

            success_messages.forEach(success_message => {
                success_message.innerHTML = result.message
                success_message.style.display = 'block'
                fadeAnimate(success_message)
            })

            document.querySelectorAll('.js_audit_field').forEach(el => el.value = '')
            document.querySelectorAll('.js-audit-request-form').forEach(el => el.style.display = 'none')

            scrollToElementWithOffset(the_success_message)
        } catch (errorResponse) {
            success_messages.forEach(success_message => {
                success_message.style.display = 'none'
            })

            error_message.innerHTML = errorResponse.message
            error_message.style.display = 'block'
            fadeAnimate(error_message)

            scrollToElementWithOffset(error_message)
        } finally {
            document.querySelectorAll('.js_audit_field').forEach(el => el.disabled = false)
            document.querySelectorAll('.js_audit_submit').forEach(el => el.disabled = false)
        }
    })
})
